/** Admin API calls - template download + xlsx upload (multipart) + catalog/user CRUD (JSON). */
import { API_BASE } from './config';
import { readAuth, apiFetch } from './apiClient';

function authHeaders() {
  const token = readAuth()?.accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function downloadTemplate() {
  const res = await fetch(`${API_BASE}/api/admin/upload/template`, { headers: authHeaders() });
  if (!res.ok) throw new Error(`Template download failed (${res.status})`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'FarmGate_Upload_Template.xlsx';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

async function postUpload(path, file, mode) {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('mode', mode);
  // Note: do NOT set Content-Type - the browser sets the multipart boundary.
  const res = await fetch(`${API_BASE}${path}`, { method: 'POST', headers: authHeaders(), body: fd });
  if (!res.ok) {
    let detail;
    try {
      detail = (await res.json()).detail;
    } catch {
      /* non-JSON */
    }
    throw new Error(detail || `Request failed (${res.status})`);
  }
  return res.json();
}

export const validateUpload = (file, mode) => postUpload('/api/admin/upload/validate', file, mode);
export const processUpload = (file, mode) => postUpload('/api/admin/upload/process', file, mode);

/**
 * How long since prices arrived, and since any were observed. Two different
 * questions: the observation age is what customers see and what the weekly
 * digest keys off, the upload age says whether anybody is still sending
 * spreadsheets at all.
 */
export const getUploadFreshness = () => apiFetch('/api/admin/upload/freshness');

export const getUploadHistory = () =>
  fetch(`${API_BASE}/api/admin/upload/history`, { headers: authHeaders() }).then((r) => {
    if (!r.ok) throw new Error(`History failed (${r.status})`);
    return r.json();
  });

// Server rejects larger bodies with 413; checking here saves the round-trip.
export const MAX_UPLOAD_BYTES = 15 * 1024 * 1024;

export const getUploadHistoryPaged = (limit, offset) =>
  fetch(`${API_BASE}/api/admin/upload/history/paged?limit=${limit}&offset=${offset}`, {
    headers: authHeaders(),
  }).then((r) => {
    if (!r.ok) throw new Error(`History failed (${r.status})`);
    return r.json();
  });

export const getUploadDetail = (uploadId) =>
  fetch(`${API_BASE}/api/admin/upload/history/${uploadId}`, { headers: authHeaders() }).then((r) => {
    if (!r.ok) throw new Error(`Upload detail failed (${r.status})`);
    return r.json();
  });

export async function downloadUploadFile(uploadId, filename) {
  const res = await fetch(`${API_BASE}/api/admin/upload/history/${uploadId}/file`, {
    headers: authHeaders(),
  });
  if (!res.ok) {
    let detail;
    try {
      detail = (await res.json()).detail;
    } catch {
      /* non-JSON */
    }
    throw new Error(detail || `File download failed (${res.status})`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'upload.xlsx';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ---- Catalog CRUD (admin) ----
export const createCategory = (body) => apiFetch('/api/admin/categories', { method: 'POST', body });
export const updateCategory = (id, body) => apiFetch(`/api/admin/categories/${id}`, { method: 'PUT', body });
export const deleteCategory = (id) => apiFetch(`/api/admin/categories/${id}`, { method: 'DELETE' });

export const createCommodity = (body) => apiFetch('/api/admin/commodities', { method: 'POST', body });
export const updateCommodity = (id, body) => apiFetch(`/api/admin/commodities/${id}`, { method: 'PUT', body });
export const deleteCommodity = (id) => apiFetch(`/api/admin/commodities/${id}`, { method: 'DELETE' });

// States. The code is the primary key and markets join on it, so it cannot be
// edited; delete is refused while any market is in that state.
export const createState = (body) => apiFetch('/api/admin/states', { method: 'POST', body });
export const updateState = (code, body) => apiFetch(`/api/admin/states/${code}`, { method: 'PUT', body });
export const deleteState = (code) => apiFetch(`/api/admin/states/${code}`, { method: 'DELETE' });

// Markets. Delete is refused server-side when price rows reference the market,
// so retiring is the usual way to take one out of circulation.
export const createMarket = (body) => apiFetch('/api/admin/markets', { method: 'POST', body });
export const updateMarket = (id, body) => apiFetch(`/api/admin/markets/${id}`, { method: 'PUT', body });
export const deleteMarket = (id) => apiFetch(`/api/admin/markets/${id}`, { method: 'DELETE' });

export const createAlias = (body) => apiFetch('/api/admin/aliases', { method: 'POST', body });
export const updateAlias = (id, body) => apiFetch(`/api/admin/aliases/${id}`, { method: 'PUT', body });
export const deleteAlias = (id) => apiFetch(`/api/admin/aliases/${id}`, { method: 'DELETE' });

// ---- User CRUD (admin) ----
export const getUsers = () => apiFetch('/api/auth/users');
export const inviteUser = (body) => apiFetch('/api/auth/users', { method: 'POST', body });
/**
 * Send an invitation again. Only a pending account can be resent to, and
 * issuing the new link invalidates the previous one, so an invite that expired
 * or went to spam can be recovered without deleting and recreating the account.
 */
export const resendInvite = (id) =>
  apiFetch(`/api/auth/users/${id}/resend-invite`, { method: 'POST' });

export const updateUser = (id, body) => apiFetch(`/api/auth/users/${id}`, { method: 'PUT', body });
export const deleteUser = (id) => apiFetch(`/api/auth/users/${id}`, { method: 'DELETE' });
